import { Component, Input } from '@angular/core';

import { cva } from '../utils/class-variance.util';
// ThemeSection type is missing, so use a union string literal for strict typing
import { ProgressVariant, ProgressSize, ProgressType } from '../models/progress.model';

const progressBarVariants = cva({
  base: [
    'relative w-full overflow-hidden rounded-full',
    'backdrop-blur-md border transition-all duration-500 ease-out',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-60 before:rounded-full before:pointer-events-none',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      default: ['bg-gray-200/50 border-gray-300/30', 'dark:bg-gray-700/50 dark:border-gray-600/30'].join(' '),
      primary: ['bg-primary-100/50 border-primary-200/30', 'dark:bg-primary-900/50 dark:border-primary-800/30'].join(
        ' ',
      ),
      secondary: ['bg-gray-100/50 border-gray-200/30', 'dark:bg-gray-800/50 dark:border-gray-700/30'].join(' '),
      success: ['bg-cyan-100/50 border-cyan-200/30', 'dark:bg-green-900/50 dark:border-green-800/30'].join(' '),
      warning: ['bg-yellow-100/50 border-yellow-200/30', 'dark:bg-yellow-900/50 dark:border-yellow-800/30'].join(' '),
      error: ['bg-red-100/50 border-red-200/30', 'dark:bg-red-900/50 dark:border-red-800/30'].join(' '),
      premium: [
        'bg-linear-to-r from-primary-100/50 via-purple-100/50 to-primary-100/50 border-primary-200/40',
        'dark:bg-linear-to-r dark:from-primary-900/50 dark:via-purple-900/50 dark:to-primary-900/50 dark:border-primary-800/40',
      ].join(' '),
    },
    size: {
      xs: 'h-1',
      sm: 'h-2',
      md: 'h-3',
      lg: 'h-4',
      xl: 'h-6',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

const progressFillVariants = cva({
  base: [
    'h-full transition-all duration-700 ease-out rounded-full',
    'relative overflow-hidden',
    // Glass morphism fill
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-40 before:rounded-full before:pointer-events-none',
  ].join(' '),
  variants: {
    variant: {
      default: ['bg-gray-600 shadow-lg shadow-gray-600/30', 'dark:bg-gray-400 dark:shadow-gray-400/30'].join(' '),
      primary: [
        'bg-linear-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/40',
        'dark:from-primary-400 dark:to-primary-500 dark:shadow-primary-400/40',
      ].join(' '),
      secondary: ['bg-gray-500 shadow-lg shadow-gray-500/30', 'dark:bg-gray-400 dark:shadow-gray-400/30'].join(' '),
      success: [
        'bg-linear-to-r from-cyan-500 to-cyan-600 shadow-lg shadow-cyan-500/40',
        'dark:from-cyan-400 dark:to-cyan-500 dark:shadow-cyan-400/40',
      ].join(' '),
      warning: [
        'bg-linear-to-r from-yellow-500 to-yellow-600 shadow-lg shadow-yellow-500/40',
        'dark:from-yellow-400 dark:to-yellow-500 dark:shadow-yellow-400/40',
      ].join(' '),
      error: [
        'bg-linear-to-r from-red-500 to-red-600 shadow-lg shadow-red-500/40',
        'dark:from-red-400 dark:to-red-500 dark:shadow-red-400/40',
      ].join(' '),
      premium: [
        'bg-linear-to-r from-primary-500 via-purple-500 to-primary-600 shadow-xl shadow-primary-500/50',
        'dark:from-primary-400 dark:via-purple-400 dark:to-primary-500 dark:shadow-primary-400/50',
      ].join(' '),
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

@Component({
  selector: 'ui-progress-bar-glass',
  standalone: true,
  imports: [],
  templateUrl: './progress-bar-glass.component.html',
  styleUrls: ['./progress-bar-glass.component.scss'],
})
export class ProgressBarGlassComponent {
  protected Math = Math;

  @Input() type: ProgressType = 'linear';
  @Input() variant: ProgressVariant = 'default';
  @Input() size: ProgressSize = 'md';
  @Input() value: number = 0;
  @Input() min: number = 0;
  @Input() max: number = 100;
  @Input() bufferValue: number | undefined;
  @Input() label: string = '';
  @Input() showLabel: boolean = false;
  @Input() showPercentage: boolean = false;
  @Input() indeterminate: boolean = false;
  @Input() circularSize: number = 120;
  @Input() strokeWidth: number = 8;
  @Input() themeSection: 'global' | 'checkout' | 'product' | 'order' | 'profile' = 'global';

  computedBarClasses(): string {
    return progressBarVariants({
      variant: this.variant,
      size: this.size,
    });
  }

  computedFillClasses(): string {
    return progressFillVariants({
      variant: this.variant,
    });
  }

  percentage(): number {
    if (this.indeterminate) return 100;
    if (this.max === this.min) return 0;
    const percent = ((this.value - this.min) / (this.max - this.min)) * 100;
    return Math.min(100, Math.max(0, percent));
  }

  /** Rounded numeric percentage for templates to consume (avoids passing Signal into Math.round). */
  roundedPercentage(): number {
    return Math.round(this.percentage());
  }

  bufferPercentage(): number {
    if (this.bufferValue === undefined) return 0;
    if (this.max === this.min) return 0;
    return Math.min(100, Math.max(0, ((this.bufferValue - this.min) / (this.max - this.min)) * 100));
  }

  radius(): number {
    // Always subtract strokeWidth from circularSize, then divide by 2
    return (this.circularSize - this.strokeWidth) / 2;
  }

  circumference(): number {
    return 2 * Math.PI * this.radius();
  }

  strokeDashoffset(): number {
    const percentage = this.percentage();
    return this.circumference() - (percentage / 100) * this.circumference();
  }

  circularTrackClasses(): string {
    const variantClasses = {
      default: 'stroke-gray-200 dark:stroke-gray-700',
      primary: 'stroke-primary-200 dark:stroke-primary-800',
      secondary: 'stroke-gray-200 dark:stroke-gray-700',
      success: 'stroke-green-200 dark:stroke-green-800',
      warning: 'stroke-yellow-200 dark:stroke-yellow-800',
      error: 'stroke-red-200 dark:stroke-red-800',
      premium: 'stroke-primary-200 dark:stroke-primary-800',
    };

    return variantClasses[this.variant];
  }

  circularProgressClasses(): string {
    const variantClasses = {
      default: 'stroke-gray-600 dark:stroke-gray-400',
      primary: 'stroke-primary-500 dark:stroke-primary-400',
      secondary: 'stroke-gray-500 dark:stroke-gray-400',
      success: 'stroke-green-500 dark:stroke-green-400',
      warning: 'stroke-yellow-500 dark:stroke-yellow-400',
      error: 'stroke-red-500 dark:stroke-red-400',
      premium: 'stroke-primary-500 dark:stroke-primary-400',
    };

    return `${variantClasses[this.variant]} drop-shadow-lg`;
  }
}

/**
 * Usage Examples:
 *
 * Basic linear progress:
 * <ui-progress-bar-glass [value]="50"></ui-progress-bar-glass>
 *
 * With label and percentage:
 * <ui-progress-bar-glass
 *   [value]="75"
 *   label="Upload Progress"
 *   [showLabel]="true"
 *   [showPercentage]="true"
 *   variant="primary"
 * ></ui-progress-bar-glass>
 *
 * Indeterminate loading:
 * <ui-progress-bar-glass
 *   [indeterminate]="true"
 *   variant="primary"
 *   label="Loading..."
 *   [showLabel]="true"
 * ></ui-progress-bar-glass>
 *
 * Circular progress:
 * <ui-progress-bar-glass
 *   type="circular"
 *   [value]="65"
 *   [showPercentage]="true"
 *   variant="success"
 * ></ui-progress-bar-glass>
 *
 * Different variants:
 * <ui-progress-bar-glass [value]="30" variant="error" size="lg"></ui-progress-bar-glass>
 * <ui-progress-bar-glass [value]="80" variant="success" size="sm"></ui-progress-bar-glass>
 * <ui-progress-bar-glass [value]="90" variant="premium" size="xl"></ui-progress-bar-glass>
 *
 * E-commerce examples:
 *
 * Order progress:
 * <ui-progress-bar-glass
 *   [value]="orderProgress"
 *   label="Order Processing"
 *   [showLabel]="true"
 *   [showPercentage]="true"
 *   variant="primary"
 *   size="lg"
 * ></ui-progress-bar-glass>
 *
 * Stock level:
 * <ui-progress-bar-glass
 *   [value]="stockPercentage"
 *   label="Stock Level"
 *   [showLabel]="true"
 *   [variant]="stockPercentage < 20 ? 'error' : stockPercentage < 50 ? 'warning' : 'success'"
 *   size="sm"
 * ></ui-progress-bar-glass>
 *
 * Upload progress:
 * <ui-progress-bar-glass
 *   [value]="uploadProgress"
 *   [bufferValue]="bufferProgress"
 *   label="Uploading product images..."
 *   [showLabel]="true"
 *   [showPercentage]="true"
 *   variant="premium"
 * ></ui-progress-bar-glass>
 *
 * Checkout steps:
 * <ui-progress-bar-glass
 *   type="circular"
 *   [value]="(currentStep / totalSteps) * 100"
 *   [showPercentage]="false"
 *   label="Step {{ currentStep }} of {{ totalSteps }}"
 *   variant="primary"
 *   [circularSize]="80"
 * ></ui-progress-bar-glass>
 */
